import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { diag } from '../services/diagnostics';

// --- Types ---

interface User {
  username: string;
  userId: string;
  email?: string;
  given_name?: string;
  family_name?: string;
}

interface AuthTokens {
  accessToken: string;
  // Nexus may not always return idToken (OAuth providers do, password
  // login may not). Code falls back to parsing accessToken for the user
  // profile claims if idToken is missing.
  idToken?: string;
  refreshToken?: string;
  expiresAt?: number;
}

/**
 * Result types for the self-service auth flows (Phase F4, 2026-05-25).
 *
 * Nexus's `/auth/register` returns one of three positive shapes (201
 * OK with userId, 409 EMAIL_EXISTS, 409 PENDING_VERIFICATION) — the
 * caller needs to distinguish these to route the user correctly
 * (verify-email screen vs. login screen vs. error toast). We surface
 * them as a tagged union rather than throwing on the 409s so the
 * SignUp page can branch on `result.kind` instead of parsing error
 * messages.
 *
 * `/auth/reset-password` and `/auth/verify-email` keep the simpler
 * throw-on-failure shape since callers handle them as one success
 * path; weak-password failures hydrate a `WeakPasswordError` so the
 * caller can list the policy reasons.
 *
 * `/auth/forgot-password` is anti-enumeration on the server — it
 * ALWAYS returns 200 regardless of whether the email exists, so the
 * SPA can't be used as an account-enumeration oracle. The UI shows
 * the same "if an account exists…" message either way.
 */
// BOLUS_NOTES-4 (2026-09-16): Nexus /auth/login answers { code, message }
// (INVALID_CREDENTIALS, ACCOUNT_LOCKED, EMAIL_NOT_VERIFIED, APP_NOT_REGISTERED,
// APP_PENDING_APPROVAL, PASSWORD_CHANGE_REQUIRED …). Surface a sentence a
// parent can act on instead of axios's "Request failed with status code 401".
export function friendlyAuthError(err: any): string {
  const data = err?.response?.data;
  const code: string | undefined = data?.code;
  const status: number | undefined = err?.response?.status;
  switch (code) {
    case 'INVALID_CREDENTIALS':
      return 'Email or password is incorrect. After 5 failed attempts the account locks for 30 minutes.';
    case 'ACCOUNT_LOCKED':
      return 'This account is temporarily locked after too many failed sign-ins. Wait 30 minutes or use "Forgot password?".';
    case 'ACCOUNT_DISABLED':
      return 'This account is disabled. Contact the account owner.';
    case 'EMAIL_NOT_VERIFIED':
      return 'Please verify your email address first — check your inbox for the verification code.';
    case 'APP_NOT_REGISTERED':
      return 'This account is not registered for BolusNotes yet.';
    case 'APP_PENDING_APPROVAL':
      return 'Your BolusNotes access is pending approval.';
    case 'PASSWORD_CHANGE_REQUIRED':
      return 'A password change is required before signing in. Use "Forgot password?".';
    default:
      break;
  }
  if (data?.message) return String(data.message);
  if (data?.error) return String(data.error);
  if (!err?.response) return 'Could not reach the BolusNotes server. Check your connection and try again.';
  if (status === 401) return 'Email or password is incorrect.';
  if (status && status >= 500) return 'The BolusNotes server had a problem. Please try again in a moment.';
  return err?.message || 'Failed to sign in';
}

export type RegisterResult =
  | { kind: 'ok'; userId: string; email: string }
  | { kind: 'email_exists'; email: string }
  | { kind: 'pending_verification'; email: string; userId?: string };

export class WeakPasswordError extends Error {
  public readonly code = 'WEAK_PASSWORD' as const;
  public readonly reasons: string[];
  constructor(reasons: string[]) {
    super(reasons[0] || 'password does not meet policy');
    this.reasons = reasons;
    this.name = 'WeakPasswordError';
  }
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => void;
  signOut: () => Promise<void>;
  clearError: () => void;
  // F4 — self-service flows
  register: (
    email: string,
    password: string,
    givenName?: string,
    familyName?: string,
  ) => Promise<RegisterResult>;
  verifyEmail: (email: string, code: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (
    email: string,
    code: string,
    newPassword: string,
  ) => Promise<void>;
}

// --- Configuration ---
//
// Nexus cutover 2026-05-25 (Phase F):
//   - GATEWAY_URL default flipped from legacy K-Stack to nexus-dev.kstack.app.
//   - APP_ID renamed from 'bolus-notes' (legacy URL slug) to 'bolus_notes'
//     (Nexus PG-safe identifier; required by schema-runner.assertSafeIdentifier).
//   - APP_KEY is now an `nxsk_…` value minted via admin-mint-app-key.ts.
//   - Sent as the `x-nexus-app-key` header (NOT in the JSON body as before).

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_URL || 'https://nexus-dev.kstack.app';
const APP_KEY = process.env.REACT_APP_BOLUSNOTES_KEY || 'bolusnotes-web-dev';
const APP_ID = 'bolus_notes';

// --- Context ---

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedTokens = localStorage.getItem('bolusnotes_auth_tokens');
      if (storedTokens) {
        try {
          const tokens: AuthTokens = JSON.parse(storedTokens);
          // Prefer idToken (carries user profile claims) but fall back to
          // accessToken if absent — Nexus's password-login may not return
          // idToken depending on provider config.
          const jwtForProfile = tokens.idToken || tokens.accessToken;
          if (jwtForProfile) {
            const userProfile = parseJwt(jwtForProfile);
            setUser({
              username: userProfile.preferred_username || userProfile.email,
              userId: userProfile.sub,
              email: userProfile.email,
              given_name: userProfile.given_name,
              family_name: userProfile.family_name,
            });
          }
        } catch (err) {
          console.error('Failed to parse stored tokens', err);
          localStorage.removeItem('bolusnotes_auth_tokens');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const handleSignIn = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);

      // Nexus /auth/login reads the app-key from the `x-nexus-app-key`
      // header (not from the JSON body, like legacy K-Stack did). Body
      // carries credentials AND the `appId` field so Nexus issues a JWT
      // bound to this app — without `appId`, Nexus authenticates the user
      // but the resulting JWT has no app scope, and every subsequent call
      // to /v1/app/<appId>/fn/<handler> returns 401 even for platform admins.
      // (2026-05-27 #147 — iOS sends this; web was missing it.)
      // Response shape: { accessToken, refreshToken, expiresIn, idToken, user }
      const response = await axios.post(
        `${GATEWAY_URL}/auth/login`,
        { email, password, appId: APP_ID },
        { headers: { 'x-nexus-app-key': APP_KEY } },
      );

      const tokens: AuthTokens = response.data;
      localStorage.setItem('bolusnotes_auth_tokens', JSON.stringify(tokens));
      void diag.refreshFlag().then(() => diag.log('auth', 'login'));

      const jwtForProfile = tokens.idToken || tokens.accessToken;
      const userProfile = parseJwt(jwtForProfile);
      setUser({
        username: userProfile.preferred_username || userProfile.email,
        userId: userProfile.sub,
        email: userProfile.email,
        given_name: userProfile.given_name,
        family_name: userProfile.family_name,
      });

    } catch (err: any) {
      console.error('Sign in error:', err);
      const message = friendlyAuthError(err);
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignInWithGoogle = () => {
    // Nexus OAuth initiate: GET /auth/oauth/google?redirect_uri=…&app_id=…
    //
    // Nexus's oauth/routes.ts reads `redirect_uri` (snake_case) and
    // `app_id` (snake_case) from the query string. It does NOT read an
    // app-key on initiate — auth there happens by storing the redirect
    // URL in a state cookie, then validating it on callback. The app-key
    // is only required on subsequent API calls (via x-nexus-app-key).
    //
    // Legacy K-Stack used camelCase `redirectUri` + `appKey`; both are
    // ignored by Nexus, which is why the smoke test 302'd the user to
    // nexus-dev.kstack.app instead of dev.bolusnotes.com.
    const params = new URLSearchParams({
      redirect_uri: window.location.origin + '/oauth-callback',
      app_id: APP_ID,
    });
    window.location.href = `${GATEWAY_URL}/auth/oauth/google?${params.toString()}`;
  };

  // BOLUS_NOTES-27 (2026-09-16): real sign-out. Revoke this session's refresh
  // token on Nexus (POST /auth/logout — single-session revoke), then clear
  // every local trace so the next visitor on this browser has to sign in.
  // Server errors never block the local sign-out.
  const handleSignOut = async () => {
    setError(null);
    let refreshToken: string | undefined;
    try {
      const stored = localStorage.getItem('bolusnotes_auth_tokens');
      refreshToken = stored ? JSON.parse(stored)?.refreshToken : undefined;
    } catch { /* ignore parse errors — we clear below anyway */ }
    try {
      if (refreshToken) {
        await axios.post(
          `${GATEWAY_URL}/auth/logout`,
          { refreshToken },
          { headers: { 'x-nexus-app-key': APP_KEY }, timeout: 5000 },
        );
      }
    } catch (err: any) {
      console.warn('Sign out: server revoke failed (continuing with local sign-out):', err?.message || err);
    } finally {
      diag.log('auth', 'logout'); await diag.flush();
      localStorage.removeItem('bolusnotes_auth_tokens');
      try { sessionStorage.clear(); } catch { /* ignore */ }
      setUser(null);
    }
  };

  const clearError = () => {
    setError(null);
  };

  // ──────────────────────────────────────────────────────────────────
  // F4 — self-service flows (signup, verify, forgot, reset)
  //
  // Common headers: every call carries `x-nexus-app-key` so the
  // gateway's per-app middleware can attribute the request, and the
  // CORS preflight allowlist (Authorization, Content-Type, Accept,
  // x-nexus-app-key — hot-patched 2026-05-25 in /home/ekiss71/nexus/
  // kstack-dev/gateway/src/core/auth/cors.ts) lets the header
  // through. We pass `appId: APP_ID` in register so Nexus's Wave 4-lite
  // `user_app_associations` row gets stamped on signup (so the
  // Platform Users page can show 'bolus_notes' as the originating
  // app even though the user never owned a pump).
  //
  // Why these are NOT bearer-protected on the server: every endpoint
  // is mounted under /auth in gateway/src/index.ts BEFORE the per-app
  // bearer middleware. The server-side anti-enumeration discipline
  // (forgot-password always 200, reset/verify INVALID_CODE on
  // unknown email) is what protects them — see auth/routes.ts.

  const authHeaders = { 'x-nexus-app-key': APP_KEY } as const;

  const handleRegister = async (
    email: string,
    password: string,
    givenName?: string,
    familyName?: string,
  ): Promise<RegisterResult> => {
    try {
      const response = await axios.post(
        `${GATEWAY_URL}/auth/register`,
        {
          email,
          password,
          ...(givenName ? { givenName } : {}),
          ...(familyName ? { familyName } : {}),
          appId: APP_ID,
        },
        { headers: authHeaders, validateStatus: () => true },
      );

      // 201 — created
      if (response.status === 201 && response.data?.code === 'OK') {
        return {
          kind: 'ok',
          userId: response.data.userId,
          email: response.data.email,
        };
      }

      // 409 EMAIL_EXISTS — fully-verified user already on file
      if (response.status === 409 && response.data?.code === 'EMAIL_EXISTS') {
        return { kind: 'email_exists', email };
      }

      // 409 PENDING_VERIFICATION — half-registered user; Nexus has
      // already re-sent the verify OTP, so the SPA just routes the
      // user to the verify screen.
      if (
        response.status === 409 &&
        response.data?.code === 'PENDING_VERIFICATION'
      ) {
        return {
          kind: 'pending_verification',
          email: response.data.email || email,
          userId: response.data.userId,
        };
      }

      // 400 WEAK_PASSWORD — surface policy reasons to the caller
      if (response.status === 400 && response.data?.code === 'WEAK_PASSWORD') {
        throw new WeakPasswordError(
          Array.isArray(response.data.errors)
            ? response.data.errors
            : ['Password does not meet policy'],
        );
      }

      // Everything else — surface message if present.
      const msg =
        response.data?.message ||
        response.data?.code ||
        `Registration failed (HTTP ${response.status})`;
      throw new Error(msg);
    } catch (err: any) {
      if (err instanceof WeakPasswordError) throw err;
      console.error('Register error:', err);
      const message =
        err.response?.data?.message || err.message || 'Failed to register';
      throw new Error(message);
    }
  };

  const handleVerifyEmail = async (email: string, code: string) => {
    try {
      const response = await axios.post(
        `${GATEWAY_URL}/auth/verify-email`,
        { email, code },
        { headers: authHeaders, validateStatus: () => true },
      );
      if (response.status === 200 && response.data?.ok) {
        return;
      }
      if (response.status === 400 && response.data?.code === 'INVALID_CODE') {
        throw new Error('The verification code is invalid or has expired.');
      }
      throw new Error(
        response.data?.message ||
          `Verification failed (HTTP ${response.status})`,
      );
    } catch (err: any) {
      console.error('Verify email error:', err);
      throw new Error(
        err.response?.data?.message || err.message || 'Failed to verify email',
      );
    }
  };

  const handleResendVerification = async (email: string) => {
    // Anti-enum: server always 200. We mirror that on the client —
    // no error path; the page just shows a confirmation toast.
    try {
      await axios.post(
        `${GATEWAY_URL}/auth/resend-verification`,
        { email },
        { headers: authHeaders, validateStatus: () => true },
      );
    } catch (err) {
      // Network/CORS errors only — swallow so the UI can keep the
      // anti-enum guarantee. Surfacing them would leak account state
      // ("worked for X but failed for Y" => Y doesn't exist).
      console.warn('resendVerification network error (suppressed):', err);
    }
  };

  const handleForgotPassword = async (email: string) => {
    try {
      await axios.post(
        `${GATEWAY_URL}/auth/forgot-password`,
        { email },
        { headers: authHeaders, validateStatus: () => true },
      );
      // Server is anti-enum — always resolves with 200. Even on 5xx
      // we resolve to keep the UI message identical.
    } catch (err) {
      console.warn('forgotPassword network error (suppressed):', err);
    }
  };

  const handleResetPassword = async (
    email: string,
    code: string,
    newPassword: string,
  ) => {
    try {
      const response = await axios.post(
        `${GATEWAY_URL}/auth/reset-password`,
        { email, code, newPassword },
        { headers: authHeaders, validateStatus: () => true },
      );
      if (response.status === 200 && response.data?.ok) {
        return;
      }
      if (response.status === 400 && response.data?.code === 'INVALID_CODE') {
        throw new Error('The reset code is invalid or has expired.');
      }
      if (response.status === 400 && response.data?.code === 'WEAK_PASSWORD') {
        throw new WeakPasswordError(
          Array.isArray(response.data.errors)
            ? response.data.errors
            : ['Password does not meet policy'],
        );
      }
      throw new Error(
        response.data?.message ||
          `Reset failed (HTTP ${response.status})`,
      );
    } catch (err: any) {
      if (err instanceof WeakPasswordError) throw err;
      console.error('Reset password error:', err);
      throw new Error(
        err.response?.data?.message || err.message || 'Failed to reset password',
      );
    }
  };

  // Helper to parse JWT without a heavy library
  const parseJwt = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      return {};
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        signIn: handleSignIn,
        signInWithGoogle: handleSignInWithGoogle,
        signOut: handleSignOut,
        clearError,
        register: handleRegister,
        verifyEmail: handleVerifyEmail,
        resendVerification: handleResendVerification,
        forgotPassword: handleForgotPassword,
        resetPassword: handleResetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
